import React from "react";

const KidBackground = ({
  variant = "aurora",
  intensity = 1,
  className = "",
}) => {
  return (
    <div className={`pointer-events-none fixed inset-0 -z-10 overflow-hidden ${className}`}>
      {variant === "aurora" && <AuroraAmber intensity={intensity} />}
      {variant === "doodles" && <DoodlePattern intensity={intensity} />}
      {variant === "confetti" && <ConfettiFloat intensity={intensity} />}
      {variant === "blobs" && <BlobParallax intensity={intensity} />}
      {variant === "sparkles" && <StarSparkles intensity={intensity} />}
    </div>
  );
};

// Aurora Amber - soft, magical glow
function AuroraAmber({ intensity = 1 }) {
  const opacity = 0.18 * intensity;
  return (
    <div
      aria-hidden
      className="absolute inset-0"
      style={{
        background:
          "radial-gradient(60rem 40rem at 20% 10%, rgba(255, 208, 70, .35), transparent 60%)," +
          "radial-gradient(50rem 30rem at 80% 0%, rgba(255, 179, 0, .30), transparent 60%)," +
          "radial-gradient(40rem 30rem at 50% 85%, rgba(255, 240, 150, .35), transparent 60%)",
        opacity,
        filter: "saturate(1.1)",
      }}
    />
  );
}

// Doodle Pattern - playful hand-drawn elements
function DoodlePattern({ intensity = 1 }) {
  const opacity = 0.15 * intensity;
  return (
    <div aria-hidden className="absolute inset-0" style={{ opacity }}>
      <svg className="w-full h-full" viewBox="0 0 800 600" fill="none">
        {/* Playful doodles */}
        <path
          d="M100 150 Q150 100 200 150 T300 150"
          stroke="#FFC107"
          strokeWidth="3"
          fill="none"
          opacity="0.6"
        />
        <circle cx="400" cy="200" r="15" fill="#FFD54F" opacity="0.5" />
        <path
          d="M500 100 Q550 80 600 100 Q650 120 700 100"
          stroke="#FFB300"
          strokeWidth="2"
          fill="none"
          opacity="0.4"
        />
        <path
          d="M150 400 Q200 350 250 400 Q300 450 350 400"
          stroke="#FFF59D"
          strokeWidth="2"
          fill="none"
          opacity="0.5"
        />
        <circle cx="600" cy="450" r="20" fill="#FFC107" opacity="0.3" />
        <path
          d="M50 300 Q100 250 150 300 T250 300"
          stroke="#FFD54F"
          strokeWidth="2"
          fill="none"
          opacity="0.4"
        />
      </svg>
    </div>
  );
}

// Confetti Float - celebratory shapes drifting
function ConfettiFloat({ intensity = 1 }) {
  const count = Math.round(20 * intensity);
  const items = Array.from({ length: count });
  return (
    <div aria-hidden className="absolute inset-0">
      <style>{`
        @keyframes floatUp {
          0% { transform: translateY(10vh) rotate(0deg); opacity: 0; }
          10% { opacity: .7; }
          100% { transform: translateY(-110vh) rotate(360deg); opacity: 0; }
        }
        @media (prefers-reduced-motion: reduce) {
          * { animation-duration: 0.001ms !important; animation-iteration-count: 1 !important; }
        }
      `}</style>
      {items.map((_, i) => {
        const left = Math.random() * 100;
        const delay = Math.random() * 8;
        const dur = 8 + Math.random() * 6;
        const size = 6 + Math.random() * 10;
        const colors = ["#FFC107", "#FFD54F", "#FFB300", "#FFF59D"];
        const color = colors[i % colors.length];
        const borderRadius = `${Math.round(Math.random() * 50)}%`;
        return (
          <span
            key={i}
            className="absolute"
            style={{
              left: `${left}%`,
              bottom: "-5vh",
              width: size,
              height: size,
              background: color,
              opacity: 0.8,
              borderRadius,
              filter: "drop-shadow(0 2px 6px rgba(255,193,7,.35))",
              animation: `floatUp ${dur}s linear ${delay}s infinite`,
            }}
          />
        );
      })}
    </div>
  );
}

// Blob Parallax - friendly morphing blobs
function BlobParallax({ intensity = 1 }) {
  const opacity = 0.25 * intensity;
  return (
    <svg aria-hidden className="absolute inset-0 w-full h-full" viewBox="0 0 800 600">
      <defs>
        <linearGradient id="amberGrad" x1="0" x2="1" y1="0" y2="1">
          <stop offset="0%" stopColor="#FFD54F"/>
          <stop offset="100%" stopColor="#FFC107"/>
        </linearGradient>
      </defs>
      <g style={{ opacity }}>
        <Blob cx={200} cy={200} r={140} delay={0} />
        <Blob cx={600} cy={320} r={170} delay={2} />
        <Blob cx={420} cy={120} r={110} delay={4} />
      </g>
    </svg>
  );
}

function Blob({ cx, cy, r, delay = 0 }) {
  return (
    <>
      <defs>
        <filter id="blur" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="20" />
        </filter>
      </defs>
      <circle cx={cx} cy={cy} r={r} fill="url(#amberGrad)" filter="url(#blur)">
        <animate attributeName="r" values={`${r};${r * 1.08};${r}`} dur="12s" repeatCount="indefinite" begin={`${delay}s`} />
        <animate attributeName="cx" values={`${cx};${cx + 12};${cx}`} dur="10s" repeatCount="indefinite" begin={`${delay}s`} />
        <animate attributeName="cy" values={`${cy};${cy - 10};${cy}`} dur="11s" repeatCount="indefinite" begin={`${delay}s`} />
      </circle>
    </>
  );
}

// Star Sparkles - twinkling stars
function StarSparkles({ intensity = 1 }) {
  const count = Math.round(15 * intensity);
  const stars = Array.from({ length: count });
  return (
    <div aria-hidden className="absolute inset-0">
      <style>{`
        @keyframes twinkle {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.2); }
        }
        @media (prefers-reduced-motion: reduce) {
          * { animation-duration: 0.001ms !important; animation-iteration-count: 1 !important; }
        }
      `}</style>
      {stars.map((_, i) => {
        const left = Math.random() * 100;
        const top = Math.random() * 100;
        const delay = Math.random() * 3;
        const size = 2 + Math.random() * 4;
        const colors = ["#FFC107", "#FFD54F", "#FFB300", "#FFF59D"];
        const color = colors[i % colors.length];
        return (
          <div
            key={i}
            className="absolute rounded-full"
            style={{
              left: `${left}%`,
              top: `${top}%`,
              width: size,
              height: size,
              background: color,
              boxShadow: `0 0 ${size * 2}px ${color}`,
              animation: `twinkle ${2 + Math.random() * 2}s ease-in-out ${delay}s infinite`,
            }}
          />
        );
      })}
    </div>
  );
}

export default KidBackground;
